import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useDriverJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchJobs = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error: err } = await supabase
        .from('bookings')
        .select(`
          *,
          profiles:customer_id(full_name, phone),
          driver_assignments!inner(driver_id, task_type)
        `)
        .eq('driver_assignments.driver_id', user.id)
        .order('pickup_date', { ascending: true });

      if (err) throw err;
      setJobs(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchJobs();

    // Realtime subscription
    const channel = supabase
      .channel('driver-bookings')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'bookings',
      }, () => fetchJobs())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [fetchJobs]);

  const updateStatus = async (bookingId, newStatus) => {
    const { error: err } = await supabase
      .from('bookings')
      .update({ status: newStatus })
      .eq('id', bookingId);
    if (!err) await fetchJobs();
    return { error: err };
  };

  const pendingJobs = jobs.filter(j =>
    ['assigned', 'driver_accepted', 'driver_on_the_way', 'driver_reached_pickup', 'pickup_started'].includes(j.status)
  );
  const completedJobs = jobs.filter(j =>
    ['pickup_completed', 'processing', 'ready_for_delivery', 'out_for_delivery', 'delivered'].includes(j.status)
  );

  return { jobs, pendingJobs, completedJobs, loading, error, fetchJobs, updateStatus };
}